import DialogForm from "../../components/common/DialogForm/DialogForm";
import Form from "../../components/common/Form/Form";
import { useAlert } from "../../context/AlertContext";
import { Alert, Box, FormControl, FormGroup } from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import PasswordRules from "../../components/auth/PasswordRules/PasswordRules";
import SecretInput from "../../components/auth/SecretInput";
import { useEffect } from "react";
import { useAdminResetPassword } from "../../hooks/useUser";
import {
  AdminResetUserPasswordRequest,
  adminResetUserPasswordSchema,
} from "../../types/user/AdminResetPassword";

interface AdminResetPasswordDialogProps {
  userId: string;
  customerId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const formId = "admin-reset-password-form";

function AdminResetPasswordFields({
  form: { control, watch, reset },
  customerId,
  userId,
}: {
  form: UseFormReturn<AdminResetUserPasswordRequest>;
  customerId: string;
  userId: string;
}) {
  useEffect(() => {
    reset({
      customerId,
      userId,
      temporaryPassword: "",
    });
  }, [customerId, userId, reset]);

  const temporaryPassword = watch("temporaryPassword");

  return (
    <Box className="flex flex-col gap-y-4">
      <FormGroup>
        <Box className="flex flex-col gap-y-2">
          <FormControl>
            <Controller
              name={"temporaryPassword"}
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <SecretInput
                  {...field}
                  label="Temporary Password"
                  error={!!fieldState.error}
                  autoFocus
                  autoComplete="off"
                />
              )}
            />
          </FormControl>
          <PasswordRules
            password={temporaryPassword}
            confirmPassword={temporaryPassword}
          />
        </Box>
      </FormGroup>
      <Alert severity="warning">
        This user will NOT be notified. Please inform them of their new
        password.
      </Alert>
    </Box>
  );
}

export function AdminResetPasswordDialog({
  customerId,
  userId,
  isOpen,
  setIsOpen,
}: AdminResetPasswordDialogProps) {
  const { mutateAsync: resetUserPasswordAsync } = useAdminResetPassword();
  const showAlert = useAlert();

  return (
    <Form
      id={formId}
      schema={adminResetUserPasswordSchema}
      onSubmit={async (data) => {
        try {
          const response = await resetUserPasswordAsync(data);
          if (response?.success) {
            showAlert("Reset user's password successfully.", "success");
            setIsOpen(false);
          } else {
            throw new Error("Password reset failed for unknown reason.");
          }
        } catch (error) {
          console.error("Error resetting user password:", error);
          showAlert("Failed to reset user password.", "error");
        }
      }}
      options={{
        defaultValues: {
          customerId,
          userId,
          temporaryPassword: "",
        },
      }}
    >
      {(form) => {
        const {
          reset,
          formState: { isSubmitting, isDirty, isValid },
        } = form;
        return (
          <DialogForm
            formId={formId}
            dialogTitle="Reset User Password"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            isSubmitting={isSubmitting}
            isSubmitDisabled={!isDirty || !isValid}
            isCancelDisabled={isSubmitting}
            afterClose={reset}
          >
            <AdminResetPasswordFields
              customerId={customerId}
              userId={userId}
              form={form}
            />
          </DialogForm>
        );
      }}
    </Form>
  );
}
